import { AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { WindyHealth } from "@shared/windy-health";

const issueLabels: Record<string, string> = {
  missing_credentials: "Missing credentials",
  invalid_credentials: "Invalid credentials",
  rate_limited: "Rate limited",
  upstream_error: "Upstream errors",
  invalid_payload: "Invalid payloads",
  slow_requests: "Slow requests",
  stale_images: "Stale images",
};

function WindyStatusBadge({ status }: { status: string }) {
  const config = status === "healthy"
    ? { color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100", icon: CheckCircle }
    : status === "degraded"
      ? { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100", icon: AlertTriangle }
      : { color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100", icon: Clock };
  const Icon = config.icon;

  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export default function WindyHealthCard({ windy }: { windy?: WindyHealth }) {
  const status = windy?.status ?? "unknown";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Windy Beach Cameras
        </CardTitle>
        <CardDescription>
          Camera provider health and image coverage observations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <WindyStatusBadge status={status} />
            {windy && windy.issues.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {windy.issues.map((issue) => issueLabels[issue] ?? issue).join(" · ")}
              </span>
            )}
            {windy && windy.issues.length === 0 && windy.status === "healthy" && (
              <span className="text-sm text-muted-foreground">No reported issues</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {windy ? `${windy.windowMinutes}-minute process-local window` : "Metrics unavailable"}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground block">HTTP attempts</span>
            <span className="font-medium">{windy?.requests ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">HTTP failures</span>
            <span className={`font-medium ${(windy?.failures ?? 0) > 0 ? "text-red-600 dark:text-red-400" : ""}`}>
              {windy?.failures ?? "—"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block">Invalid credentials</span>
            <span className="font-medium">{windy?.invalidCredentials ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Rate limits</span>
            <span className="font-medium">{windy?.rateLimits ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Upstream errors</span>
            <span className="font-medium">{windy?.upstreamErrors ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Invalid payloads</span>
            <span className="font-medium">{windy?.invalidPayloads ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Average latency</span>
            <span className="font-medium">{windy?.averageLatencyMs == null ? "—" : `${windy.averageLatencyMs}ms`}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">No-coverage lookups</span>
            <span className="font-medium">{windy?.noCoverageLookups ?? "—"}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm border-t pt-4">
          <div>
            <span className="text-muted-foreground block">Images observed</span>
            <span className="font-medium">{windy?.imagesObserved ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Stale images, over 1 hour</span>
            <span className="font-medium">{windy?.staleImages ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Excluded images, over 24 hours</span>
            <span className="font-medium">{windy?.excludedOldImages ?? "—"}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground border-t pt-4">
          <div>Last HTTP request: {windy?.lastRequestAt ? new Date(windy.lastRequestAt).toLocaleString() : "Not observed"}</div>
          <div>Last successful request: {windy?.lastSuccessAt ? new Date(windy.lastSuccessAt).toLocaleString() : "Not observed"}</div>
        </div>

        <p className="text-xs text-muted-foreground">
          Counts cover a 30-minute process-local window, bounded to the last 100 HTTP attempts and 100 camera lookups. These are observations, not unique cameras. Stale means older than 1 hour; excluded means older than 24 hours. No coastal coverage is not a provider outage. Unobserved or idle activity is unknown.
        </p>
      </CardContent>
    </Card>
  );
}
